import { TopupAttention } from "app/core/domains/billing-managment/history/topup-attention.model";
import { Injectable } from "@angular/core";
import { AppStorageService } from "shared-service/app-storage/app.storage";
import { AuthService } from "shared-service/services/auth/auth.service";
import { BehaviorSubject, catchError, from, map, Observable, tap, throwError } from "rxjs";
import { LoggerService } from "shared-service/services/logger/logger.service";
import { NotificationService } from "shared-service/services/notification/notification.service";
import { eNotificationType } from "shared-domains/utilities/notification.helper";
import { WalletInfo } from "../../../domains/billing-managment/information/wallet-info.model";
import { TopupOrder } from "../../../domains/billing-managment/history/topup-order.model";
import { VlpGrpcClients } from "shared-service/services/grpc/grpc-client.factory";
import { ExternalBillingHistoryRPCClient } from "../../../../../grpc-api/shared/external-apis/vlp-billing/vlp-billing-history-external-apis.client";
import { TopupOrderDTO } from "../../../../../grpc-api/shared/domains/vlp-billing/vlp-billing-history-model";
import { PaymentMethodDTO } from "../../../../../grpc-api/shared/domains/vlp-billing/vlp-billing-information-model";
import { CreateTopupOrderRequest, GetTopupOrdersForReviewRequest } from "../../../../../grpc-api/shared/domains/vlp-billing/vlp-billing-history-topup-request-response";
import { RpcError } from "@protobuf-ts/runtime-rpc";

/** A payment method offered to the customer (resolved server-side from the tenant configuration). */
export interface PaymentMethodInfo {
    providerCode: string;
    paymentProviderId: number;
    paymentMethodId: number;
    name: string;
    currencyCode: string;
    displayOrder: number;
    minAmount: number;
    maxAmount: number;
    bankName: string;
    bankAccountName: string;
    bankAccountNumber: string;
    /**
     * Phương thức này có tự sinh QR cho TỪNG lệnh nạp không. true ⇒ QR động đã mang sẵn số tiền và nội
     * dung chuyển khoản, giao diện KHÔNG được đè QR tĩnh lên; false (chuyển khoản thủ công) ⇒ mới rơi
     * về QR tĩnh đã cấu hình. Server suy từ adapter, client không đoán theo providerCode.
     */
    providesOwnQr: boolean;
    /** Cấu hình cho phép khách chuyển số tiền tuỳ ý ⇒ mã QR không mang số tiền. */
    allowOpenAmountTransfer: boolean;
    /** Cấu hình đã sinh ra phương thức này — khoá để lấy ảnh QR tĩnh của nó. */
    tenantPaymentConfigurationId: number;
    /** Cấu hình đó đã có QR tĩnh cho tenant hiện tại chưa. CHỈ cờ; ảnh lấy riêng khi cần vẽ. */
    hasTopupQr: boolean;
    topupQrNote: string;
}

/** Result of the backend fee computation (client never computes fees itself). */
/** Kết quả phát mã của nhà cung cấp thanh toán cho MỘT lệnh nạp. */
export interface TopupPaymentSession {
    /** Chuỗi VietQR (EMVCo) để vẽ thành ảnh QR. Rỗng = nhà cung cấp không phát mã. */
    qrPayload: string;
    /** Cổng thanh toán chuyển hướng (không dùng với VietQR). */
    paymentUrl: string;
    providerOrderId: string;
}

export interface TopupFeeResult {
    requestedAmount: number;
    fee: number;
    actualAmount: number;
    feePolicySnapshot: string;
}

import { TopupAttentionSettings } from "app/core/domains/billing-managment/history/topup-attention-settings.model";

@Injectable({ providedIn: "root" })
export class TopupOrderService {
    // -----------------------------------------------------------------------------------------------------
    // @ Private fields — reactive state & lazy gRPC client
    // -----------------------------------------------------------------------------------------------------
    private _topupOrder: BehaviorSubject<TopupOrder | null> = new BehaviorSubject(null);
    private _topupOrders: BehaviorSubject<TopupOrder[] | null> = new BehaviorSubject(null);

    private _billingGrpc?: ExternalBillingHistoryRPCClient;
    private get billingGrpc(): ExternalBillingHistoryRPCClient {
        return (this._billingGrpc ??= new ExternalBillingHistoryRPCClient(this._grpc.billing()));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Constructor
    // -----------------------------------------------------------------------------------------------------
    constructor(
        private storageService: AppStorageService,
        private authService: AuthService,
        private loggerService: LoggerService,
        private notificationService: NotificationService,
        private _grpc: VlpGrpcClients,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------
    /**
     * Getter for wallet info
     */
    get topupOrder$(): Observable<TopupOrder> {
        return this._topupOrder.asObservable();
    }

    // setAccount(account: AccountInfo) {
    //     this._account.next(account);
    // }

    /**
     * Getter for account info list
     */
    get topupOrders$(): Observable<TopupOrder[]> {
        return this._topupOrders.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods — Read / Query
    // -----------------------------------------------------------------------------------------------------

    /**
     *
     * @returns
     */
    getTopupOrders(): Observable<TopupOrder[]> {
        return this.getTopupOrdersGrpc();
    }

    getTopupOrdersByWalletId(walletId: number): Observable<TopupOrder[]> {
        return this.getTopupOrdersByWalletIdGrpc(walletId);
    }

    /** Payment methods the current customer may use for a wallet (resolved from tenant configuration). */
    getAvailablePaymentMethods(walletInfoId: number, currencyCode = ""): Observable<PaymentMethodInfo[]> {
        return from(this.billingGrpc.getAvailablePaymentMethods({ walletInfoId, currencyCode }).response).pipe(
            map((resp) => {
                this.throwIfFailed(resp.status, "GetAvailablePaymentMethods");
                return (resp.items ?? []).map((paymentMethod: PaymentMethodDTO) => ({
                    providerCode: paymentMethod.providerCode,
                    paymentProviderId: paymentMethod.paymentProviderId,
                    paymentMethodId: paymentMethod.paymentMethodId,
                    name: paymentMethod.name,
                    currencyCode: paymentMethod.currencyCode,
                    displayOrder: paymentMethod.displayOrder,
                    minAmount: Number(paymentMethod.minAmount) || 0,
                    maxAmount: Number(paymentMethod.maxAmount) || 0,
                    bankName: paymentMethod.bankName,
                    bankAccountName: paymentMethod.bankAccountName,
                    bankAccountNumber: paymentMethod.bankAccountNumber,
                    // Server chưa trả cờ (bản cũ) ⇒ coi như CÓ tự sinh QR: thà không hiện QR nào còn hơn
                    // dán QR tĩnh cạnh một cổng thanh toán sắp trả QR động của chính nó.
                    providesOwnQr: paymentMethod.providesOwnQr !== false,
                    allowOpenAmountTransfer: paymentMethod.allowOpenAmountTransfer === true,
                    tenantPaymentConfigurationId: Number(paymentMethod.tenantPaymentConfigurationId) || 0,
                    hasTopupQr: paymentMethod.hasTopupQr === true,
                    topupQrNote: paymentMethod.topupQrNote ?? "",
                } as PaymentMethodInfo));
            }),
            catchError((error) => this.logAndRethrow("Lấy danh sách phương thức thanh toán thất bại", error)),
        );
    }

    /** Accountant review queue (server-side filtered + paged). */
    getAttentionSettings(): Observable<TopupAttentionSettings> {
        return from(this.billingGrpc.getTopupAttentionSettings({}).response).pipe(map(resp => {
            this.throwIfFailed(resp.status, "GetTopupAttentionSettings");
            return new TopupAttentionSettings(resp.data);
        }));
    }
    saveAttentionSettings(value: TopupAttentionSettings): Observable<TopupAttentionSettings> {
        return from(this.billingGrpc.saveTopupAttentionSettings(value.toDto()).response).pipe(map(resp => {
            this.throwIfFailed(resp.status, "SaveTopupAttentionSettings", false);
            window.dispatchEvent(new Event('topup-policy-changed'));
            return new TopupAttentionSettings(resp.data);
        }));
    }
    getTopupAttention(take = 10): Observable<TopupAttention> {
        return from(this.billingGrpc.getTopupOrdersForReview(GetTopupOrdersForReviewRequest.create({
            attentionOnly: true, pageQuery: { skip: 0, take },
        })).response).pipe(map(resp => {
            this.throwIfFailed(resp.status, "GetTopupAttention");
            return new TopupAttention(resp);
        }));
    }

    getTopupOrdersForReview(filter: Partial<GetTopupOrdersForReviewRequest> = {}): Observable<{ items: TopupOrder[]; totalRecords: number }> {
        const req: GetTopupOrdersForReviewRequest = {
            status: filter.status ?? "",
            providerCode: filter.providerCode ?? "",
            fromDate: filter.fromDate ?? "",
            toDate: filter.toDate ?? "",
            currencyCode: filter.currencyCode ?? "",
            minAmount: filter.minAmount ?? "",
            maxAmount: filter.maxAmount ?? "",
            keyword: filter.keyword ?? "",
            pageQuery: filter.pageQuery,
            attentionOnly: filter.attentionOnly ?? false,
        };
        return from(this.billingGrpc.getTopupOrdersForReview(req).response).pipe(
            map((resp) => {
                this.throwIfFailed(resp.status, "GetTopupOrdersForReview");
                return { items: (resp.items ?? []).map((dto) => TopupOrder.fromDto(dto)), totalRecords: resp.totalRecords };
            }),
            catchError((error) => this.logAndRethrow("Lấy danh sách phiếu chờ duyệt thất bại", error)),
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods — Mutations
    // -----------------------------------------------------------------------------------------------------
    addAndUpdateTopupOrder(topupInfo: TopupOrder): Observable<TopupOrder> {
        return this.addAndUpdateTopupOrderGrpc(topupInfo);
    }

    /** Create a top-up order (state, fee and order code are assigned by the backend). */
    createTopupOrder(input: {
        walletInfoId: number;
        providerCode: string;
        requestedAmount: number;
        currencyCode?: string;
        transactionRef?: string;
        evidenceUrl?: string;
        userNote?: string;
        idempotencyKey?: string;
    }): Observable<TopupOrder> {
        const req: CreateTopupOrderRequest = {
            walletInfoId: input.walletInfoId,
            providerCode: input.providerCode,
            requestedAmount: String(input.requestedAmount ?? 0),
            currencyCode: input.currencyCode ?? "",
            transactionRef: input.transactionRef ?? "",
            evidenceUrl: input.evidenceUrl ?? "",
            userNote: input.userNote ?? "",
            idempotencyKey: input.idempotencyKey ?? "",
        };
        return from(this.billingGrpc.createTopupOrder(req).response).pipe(
            map((resp) => {
                this.throwIfFailed(resp.status, "CreateTopupOrder");
                return resp.data ? TopupOrder.fromDto(resp.data) : new TopupOrder();
            }),
            catchError((error) => this.logAndRethrow("Tạo phiếu nạp tiền thất bại", error)),
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods — khác (duyệt / xác nhận / tính phí — maker-checker)
    // -----------------------------------------------------------------------------------------------------
    approveTopupOrder(topupInfo: TopupOrder): Observable<WalletInfo> {
        return this.approveTopupOrderGrpc(topupInfo);
    }

    /** Backend-authoritative fee for a top-up (never computed on the client). */
    calculateTopupFee(walletInfoId: number, providerCode: string, requestedAmount: number, currencyCode = ""): Observable<TopupFeeResult> {
        return from(
            this.billingGrpc.calculateTopupFee({ walletInfoId, providerCode, requestedAmount: String(requestedAmount ?? 0), currencyCode }).response,
        ).pipe(
            map((resp) => {
                this.throwIfFailed(resp.status, "CalculateTopupFee");
                return {
                    requestedAmount: Number(resp.requestedAmount) || 0,
                    fee: Number(resp.fee) || 0,
                    actualAmount: Number(resp.actualAmount) || 0,
                    feePolicySnapshot: resp.feePolicySnapshot,
                } as TopupFeeResult;
            }),
            catchError((error) => this.logAndRethrow("Tính phí nạp tiền thất bại", error)),
        );
    }

    /**
     * Yêu cầu nhà cung cấp thanh toán phát mã cho một lệnh nạp đã tạo.
     *
     * <p>Chỉ gọi với phương thức TỰ SINH QR (`providesOwnQr`). Backend dựng chuỗi VietQR từ tài khoản
     * nhận tiền đã cấu hình + số tiền VND đã chốt lúc tạo lệnh + mã lệnh nạp làm nội dung chuyển khoản.
     * Trả về CHUỖI, không phải ảnh — bên gọi tự vẽ.</p>
     *
     * <p>`notifyErrors = false` khi bên gọi tự báo lỗi theo ngữ cảnh của nó (SL-1519: lệnh nạp đã tạo
     * xong, không phát được mã chỉ là cảnh báo — toast lỗi chung ở đây làm người dùng tưởng tạo hỏng).</p>
     */
    createPaymentSession(
        topupOrderId: number,
        returnUrl = "",
        options: { notifyErrors?: boolean } = {},
    ): Observable<TopupPaymentSession> {
        const notifyErrors = options.notifyErrors ?? true;
        return from(this.billingGrpc.createPaymentSession({ topupOrderId, returnUrl }).response).pipe(
            map((resp) => {
                this.throwIfFailed(resp.status, "CreatePaymentSession", notifyErrors);
                return {
                    qrPayload: resp.qrPayload ?? "",
                    paymentUrl: resp.paymentUrl ?? "",
                    providerOrderId: resp.providerOrderId ?? "",
                } as TopupPaymentSession;
            }),
            catchError((error) => this.logAndRethrow("Phát mã thanh toán thất bại", error, notifyErrors)),
        );
    }

    /** Accountant confirms a manual bank-transfer top-up and posts it to the wallet (maker/checker). */
    confirmManualTopupOrder(topupOrderId: number, adminNote = ""): Observable<TopupOrder> {
        return from(this.billingGrpc.confirmManualTopupOrder({ topupOrderId, adminNote }).response).pipe(
            map((resp) => {
                this.throwIfFailed(resp.status, "ConfirmManualTopupOrder");
                return resp.data ? TopupOrder.fromDto(resp.data) : new TopupOrder();
            }),
            catchError((error) => this.logAndRethrow("Xác nhận nạp tiền thủ công thất bại", error)),
        );
    }

    /** Accountant rejects a pending top-up. */
    rejectTopupOrder(topupOrderId: number, rejectReason = ""): Observable<TopupOrder> {
        return from(this.billingGrpc.rejectTopupOrder({ topupOrderId, rejectReason }).response).pipe(
            map((resp) => {
                this.throwIfFailed(resp.status, "RejectTopupOrder");
                return resp.data ? TopupOrder.fromDto(resp.data) : new TopupOrder();
            }),
            catchError((error) => this.logAndRethrow("Từ chối phiếu nạp tiền thất bại", error)),
        );
    }

    /** Customer cancels their own not-yet-posted top-up. */
    cancelTopupOrder(topupOrderId: number, reason = ""): Observable<TopupOrder> {
        return from(this.billingGrpc.cancelTopupOrder({ topupOrderId, reason }).response).pipe(
            map((resp) => {
                this.throwIfFailed(resp.status, "CancelTopupOrder");
                return resp.data ? TopupOrder.fromDto(resp.data) : new TopupOrder();
            }),
            catchError((error) => this.logAndRethrow("Huỷ phiếu nạp tiền thất bại", error)),
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods — gRPC transport
    // -----------------------------------------------------------------------------------------------------
    private getTopupOrdersGrpc(): Observable<TopupOrder[]> {
        return from(this.billingGrpc.getTopupOrders({}).response).pipe(
            map((resp) => {
                if (resp.status && resp.status.success === false) {
                    this.loggerService.error("Lấy danh sách phiếu nạp tiền thất bại - gRPC response:", {
                        code: resp.status.resultCode,
                        message: resp.status.message,
                        actorId: this.storageService.accountId,
                    });
                    if (resp.status.resultCode) this.notificationService.showNotificationByActionResponse(resp.status, eNotificationType.ERROR);
                    throw new Error(resp.status.message || "gRPC GetTopupOrders failed");
                }
                return (resp.items ?? []).map((dto) => TopupOrder.fromDto(dto));
            }),
            tap((orders) => this._topupOrders.next(orders)),
            catchError((error) => {
                this.loggerService.error("Lấy danh sách phiếu nạp tiền thất bại - gRPC error:", {
                    error: error?.message || error,
                    actorId: this.storageService.accountId,
                });
                return throwError(() => error);
            }),
        );
    }

    private addAndUpdateTopupOrderGrpc(topupInfo: TopupOrder): Observable<TopupOrder> {
        return from(this.billingGrpc.addAndUpdateTopupOrder(TopupOrderDTO.create(topupInfo.toDto())).response).pipe(
            map((resp) => {
                if (resp.status && resp.status.success === false) {
                    this.loggerService.error("Cập nhật nhóm phiếu nạp tiền thất bại - gRPC response:", {
                        code: resp.status.resultCode,
                        message: resp.status.message,
                        actorId: this.storageService.accountId,
                    });
                    if (resp.status.resultCode) this.notificationService.showNotificationByActionResponse(resp.status, eNotificationType.ERROR);
                    throw new Error(resp.status.message || "gRPC AddAndUpdateTopupOrder failed");
                }
                return resp.data ? TopupOrder.fromDto(resp.data) : new TopupOrder();
            }),
            catchError((error) => {
                this.loggerService.error("Cập nhật nhóm phiếu nạp tiền thất bại - gRPC error:", {
                    error: error?.message || error,
                    actorId: this.storageService.accountId,
                });
                return throwError(() => error);
            }),
        );
    }

    private approveTopupOrderGrpc(topupInfo: TopupOrder): Observable<WalletInfo> {
        return from(this.billingGrpc.approveTopupOrder(TopupOrderDTO.create(topupInfo.toDto())).response).pipe(
            map((resp) => {
                if (resp.status && resp.status.success === false) {
                    this.loggerService.error("Phê duyệt phiếu nạp tiền thất bại - gRPC response:", {
                        code: resp.status.resultCode,
                        message: resp.status.message,
                        actorId: this.storageService.accountId,
                    });
                    if (resp.status.resultCode) this.notificationService.showNotificationByActionResponse(resp.status, eNotificationType.ERROR);
                    throw new Error(resp.status.message || "gRPC ApproveTopupOrder failed");
                }
                return resp.data ? WalletInfo.fromDto(resp.data) : new WalletInfo();
            }),
            catchError((error) => {
                this.loggerService.error("Phê duyệt phiếu nạp tiền thất bại - gRPC error:", {
                    error: error?.message || error,
                    actorId: this.storageService.accountId,
                });
                return throwError(() => error);
            }),
        );
    }

    private getTopupOrdersByWalletIdGrpc(walletId: number): Observable<TopupOrder[]> {
        return from(this.billingGrpc.getTopupOrdersByWalletId({ walletId }).response).pipe(
            map((resp) => {
                if (resp.status && resp.status.success === false) {
                    this.loggerService.error("Lấy danh sách phiếu nạp tiền thất bại - gRPC response:", {
                        code: resp.status.resultCode,
                        message: resp.status.message,
                        actorId: this.storageService.accountId,
                    });
                    if (resp.status.resultCode) this.notificationService.showNotificationByActionResponse(resp.status, eNotificationType.ERROR);
                    throw new Error(resp.status.message || "gRPC GetTopupOrdersByWalletId failed");
                }
                return (resp.items ?? []).map((dto) => TopupOrder.fromDto(dto));
            }),
            catchError((error) => {
                this.loggerService.error("Lấy danh sách phiếu nạp tiền thất bại - gRPC error:", {
                    error: error?.message || error,
                    actorId: this.storageService.accountId,
                });
                return throwError(() => error);
            }),
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods — helpers
    // -----------------------------------------------------------------------------------------------------

    /** Surface a gRPC business failure as an error (also shows a toast by result code). */
    private throwIfFailed(
        status: { success?: boolean; resultCode?: string; message?: string } | undefined,
        op: string,
        notify = true,
    ): void {
        if (status && status.success === false) {
            const notified = notify && !!status.resultCode;
            if (notified) this.notificationService.showNotificationByErrorCode(status.resultCode, eNotificationType.ERROR);
            // SL-1519: đánh dấu như wallet.service — thiếu cờ này thì `logAndRethrow` bắn thêm toast
            // "UNKNOWN" thứ hai cho cùng một lỗi nghiệp vụ.
            const error: any = new Error(status.message || `gRPC ${op} failed`);
            error.__notified = notified;
            throw error;
        }
    }

    private logAndRethrow(message: string, error: any, notify = true): Observable<never> {
        this.loggerService.error(`${message} - gRPC error:`, { error: error?.message || error, actorId: this.storageService.accountId });
        // SL-1154 — lỗi TRANSPORT (RpcError: 403/502/timeout) trước đây chỉ vào console: người dùng bấm
        // "Xác nhận" xong không thấy gì, tưởng nút hỏng. `throwIfFailed` chỉ bắn toast cho lỗi nghiệp vụ
        // (status.success = false) nên nhánh này phải tự lo. Đánh dấu __notified như wallet.service để
        // component không báo trùng.
        if (notify && !error?.__notified) {
            this.notificationService.showNotificationByErrorCode(
                TopupOrderService.mapRpcErrorToMessageCode(error),
                eNotificationType.ERROR,
            );
            if (error && typeof error === "object") error.__notified = true;
        }
        return throwError(() => error);
    }

    /** Mã lỗi transport → khóa i18n `error-messages.*`; không nhận ra thì để UNKNOWN. */
    private static mapRpcErrorToMessageCode(error: unknown): string {
        const code = error instanceof RpcError ? (error.code || "").toUpperCase() : "";
        switch (code) {
            case "PERMISSION_DENIED":
            case "UNAUTHENTICATED":
            case "ABORTED":
                return code;
            default:
                return "UNKNOWN";
        }
    }
}
